import { forwardRef, useEffect, useRef, useState } from 'react';
import { ListBoxItem } from './ListBoxItem';
import { mergeSxStyle } from '../../common/mergeSxStyle';
import { useInterstateTheme } from '../../InterstateThemeProvider';
import { interstateInputEventBuilder } from '../../InterstateEvents';
import { listBoxPaperStyleBuilder } from './listBoxStyleBuilder';
import { MenuList, Paper } from '@mui/material';
import { jsx as _jsx } from "react/jsx-runtime";
export const calcNextIndex = (currIndex, optionsLength) => {
  return (currIndex + 1) % optionsLength;
};
export const focusNext = (currentIndex, options, selectedItem) => {
  let nextIndex = currentIndex;
  for (let i = 0; i < options.length; i++) {
    nextIndex = calcNextIndex(nextIndex, options.length);
    if (!options[nextIndex].disabled && !options[nextIndex].groupHeader && options[nextIndex].value !== selectedItem) {
      return nextIndex;
    }
  }
  return -1;
};
export const focusPrev = (currentIndex, options, selectedItem) => {
  let nextIndex = currentIndex;
  for (let i = 0; i < options.length; i++) {
    nextIndex = calcPrevIndex(nextIndex, options.length);
    if (!options[nextIndex].disabled && !options[nextIndex].groupHeader && options[nextIndex].value !== selectedItem) {
      return nextIndex;
    }
  }
  return -1;
};
export const calcPrevIndex = (currIndex, optionsLength) => {
  return currIndex - 1 < 0 ? optionsLength - 1 : (currIndex - 1) % optionsLength;
};
export const ListBoxMenu = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    defaultSelected = '',
    maxHeight = '600px',
    name,
    onClose,
    open,
    options,
    listBoxSx,
    listBoxItemSx,
    id,
    size = 'small',
    maxWidth = 'auto',
    minWidth = 'auto',
    doKeyAction,
    autoFocusItem = true,
    selectedItem,
    onSelectItem,
    ...rest
  } = _ref;
  const [_selectedItem, setSelectedItem] = useState(defaultSelected);
  const [activeIndex, setActiveIndex] = useState(defaultSelected ? options.findIndex(elem => elem.value === defaultSelected) ?? -1 : -1);
  useEffect(() => {
    if (!open) {
      setActiveIndex(_selectedItem ? options.findIndex(elem => elem.value === _selectedItem) ?? -1 : -1);
    }
  }, [open]);
  useEffect(() => {
    if (options.length === 1 && autoFocusItem) {
      setActiveIndex(0);
    } else {
      setActiveIndex(_selectedItem ? options.findIndex(elem => elem.value === _selectedItem) ?? -1 : -1);
    }
  }, [options, _selectedItem]);

  // perform the action
  useEffect(() => {
    doEventActions(doKeyAction ?? '');
  }, [doKeyAction]);
  useEffect(() => {
    if (selectedItem !== undefined) {
      setSelectedItem(selectedItem);
    }
  }, [selectedItem]);
  const theme = useInterstateTheme();
  const paperStyle = mergeSxStyle(listBoxPaperStyleBuilder(theme, maxHeight, maxWidth, minWidth), listBoxSx);
  const callSelectHandlers = (event, onSelect, value, label, index, itemProps, name, onMouseSelect) => {
    if (onSelectItem) {
      onSelectItem(value);
    }
    const itemEventValue = {
      itemValue: value,
      itemProps
    };
    if (onSelect) {
      onSelect(interstateInputEventBuilder(name, itemEventValue, undefined, event, label, undefined, undefined));
    }
    if (onMouseSelect) {
      onMouseSelect(interstateInputEventBuilder(name, itemEventValue, undefined, event, label, undefined, undefined));
    }
    onClose();
    setActiveIndex(index);
    setSelectedItem(value);
  };
  const handleItemClick = (onSelect, value, label, index, itemProps, name, onMouseSelect) => {
    return event => {
      callSelectHandlers(event, onSelect, value, label, index, itemProps, name, onMouseSelect);
    };
  };

  // This handler is used in the case where the mouse listener is used to select if the list box doesn't have focus
  const handleItemMouseClick = (onMouseSelect, value, label, index, itemProps, name) => {
    return event => {
      if (onSelectItem) {
        onSelectItem(value);
      }
      const itemEventValue = {
        itemValue: value,
        itemProps
      };
      // If the mouse click handler is used it won't pickup the enter selection
      // So we want to call onMouseSelect also
      if (onMouseSelect) {
        onMouseSelect(interstateInputEventBuilder(name, itemEventValue, undefined, event, label, undefined, undefined));
      }
      onClose();
      setActiveIndex(index);
      setSelectedItem(value);
    };
  };
  const onEnterSingleSelect = () => {
    const singleOption = options[0];
    if (options.length === 1 && !singleOption.groupHeader) {
      callSelectHandlers(undefined, singleOption.onSelect, singleOption.value, singleOption.label, 0, singleOption.itemProps, name, singleOption.onMouseSelect);
    }
  };
  const doEventActions = key => {
    switch (key) {
      case 'ArrowUp':
        setActiveIndex(focusPrev(activeIndex, options, _selectedItem));
        break;
      case 'ArrowDown':
        setActiveIndex(focusNext(activeIndex, options, _selectedItem));
        break;
      case 'Tab':
        setActiveIndex(focusNext(activeIndex, options, _selectedItem));
        break;
      case 'Escape':
        onClose();
        break;
      case 'Enter':
        onEnterSingleSelect();
        break;
      default:
        break;
    }
  };
  const handleKeyDown = event => {
    event.preventDefault();
    event.stopPropagation();
    doEventActions(event.key);
  };
  const onButtonKeyDown = event => {
    handleKeyDown(event);
  };

  // Used to maintain the scroll position because of an issue with MuiMenuList
  const scrollPos = useRef([0, 0]);
  scrollPos.current[0] = window.scrollX;
  scrollPos.current[1] = window.scrollY;
  const optionsToDisplay = options.map((_ref2, index) => {
    let {
      divider,
      disabled,
      groupHeader,
      label,
      value,
      onSelect,
      onMouseSelect,
      itemProps,
      id,
      ...rest
    } = _ref2;
    const effectiveItemId = id ? `listbox-item-${id}-${label?.replace(/\s/g, '') || index}` : `listbox-item-${label?.replace(/\s/g, '') || index}`;
    const effectiveDataTestId = id ? `listbox-item-${rest['data-testid']}-${label?.replace(/\s/g, '') || index}` : `listbox-item-${label?.replace(/\s/g, '') || index}`;
    const hasAutoFocus = index === activeIndex;
    /**
     * The key is setup so that the item will re-render when the autofocus changes, necessary for SelectInput
     */
    return /*#__PURE__*/_jsx(ListBoxItem, {
      selected: Boolean(value && _selectedItem && value === _selectedItem && !groupHeader && !disabled),
      onClick: !groupHeader ? handleItemClick(onSelect, value, label, index, itemProps, name, onMouseSelect) : undefined,
      onMouseClick: groupHeader ? undefined : onSelect ? handleItemClick(onSelect, value, label, index, itemProps, name, onMouseSelect) : handleItemMouseClick(onMouseSelect, value, label, index, itemProps, name),
      onKeyDown: onButtonKeyDown,
      divider: divider,
      label: label,
      value: value,
      disabled: disabled,
      autoFocus: index === activeIndex,
      sx: listBoxItemSx,
      id: effectiveItemId,
      "data-testid": effectiveDataTestId,
      size: size,
      groupHeader: groupHeader
    }, `${label}-${hasAutoFocus}`);
  });
  return /*#__PURE__*/_jsx(Paper, {
    sx: paperStyle,
    className: 'interstate-listbox-root',
    children: /*#__PURE__*/_jsx(MenuList, {
      variant: 'menu',
      id: id,
      "data-testid": rest['data-testid'],
      autoFocus: false,
      onKeyDown: onButtonKeyDown,
      onFocus: () => {
        window.scrollTo(...scrollPos.current);
      },
      autoFocusItem: autoFocusItem,
      children: optionsToDisplay
    })
  });
});
ListBoxMenu.displayName = 'ListBoxMenu';